import { parseTransactionIds } from "helpers/transactionIdFilter";
import { paymentStatusLabel, paymentStatusOptions } from "helpers/paymentLifecycle";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "store/index";
import {
  getDepositsList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  getDepositStatusesList,
  modifyDepositStatus,
} from "store/payments/deposits/slice";
import { depositsListLimit, depositsListLimits } from "configs/limits";

import Pagination from "components/Pagination";
import DepositFilters, {
  toIsoDateTime,
} from "./components/DepositFilters";

import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { handleApiError } from "helpers/handleApiError";
import { showNotification, NotificationTypes } from "store/notifications/slice";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissions } from "helpers/usePermission";
import styles from "./List.module.scss";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";


const compactReference = (value: string, startLength = 10, endLength = 8) => {
  if (value.length <= startLength + endLength + 3) return value;

  return `${value.slice(0, startLength)}...${value.slice(-endLength)}`;
};

const DepositsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const [currentLimit, setCurrentLimit] = useState(depositsListLimit);

  const [order, setOrder] = useState<{
    userId: string;
    amountAttempt: string;
    id: string;
    fee: string;
    currency: string;
    status: string;
    depositType: string;
    paymentMethod: string;
    created: string;
    completed: string;
  }>({
    userId: "none",
    amountAttempt: "none",
    id: "none",
    fee: "none",
    currency: "none",
    status: "none",
    depositType: "none",
    paymentMethod: "none",
    created: "none",
    completed: "none",
  });
  const [providers, setProviders] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [methods, setMethods] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [currencies, setCurrencies] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [statuses, setStatuses] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [types, setTypes] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [depositId, setDepositId] = useState("");
  const [userId, setUserId] = useState("");
  const [externalRef, setExternalRef] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [completedFrom, setCompletedFrom] = useState("");
  const [completedTo, setCompletedTo] = useState("");

  const getNumericFilter = (value: string) => {
    return parseTransactionIds(value)[0];
  };

  const paymentsDepositList = useAppSelector(
    (store) => store.paymentsDeposit.list,
  );
  const isListFetched = useAppSelector(
    (store) => store.paymentsDeposit.isListFetched,
  );
  const totalItems = useAppSelector(
    (store) => store.paymentsDeposit.totalItems,
  );
  const totalPages = useAppSelector(
    (store) => store.paymentsDeposit.totalPages,
  );
  const activePage = useAppSelector(
    (store) => store.paymentsDeposit.activePage,
  );
  const selectedItems = useAppSelector(
    (store) => store.paymentsDeposit.selectedItems,
  );
  const processingItemId = useAppSelector(
    (store) => store.paymentsDeposit.processingItemId,
  );

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'paymentsDeposit',
    isListFetched
  });

  const statusesList = useAppSelector(
    (store) => store.paymentsDeposit.statusesList,
  );

  const activePermissions = usePermissions("payments_transactions_deposits");
  const canUpdate =
    activePermissions !== null && activePermissions.includes("update");



  const hasOrder = Object.values(order).some(
    (value) => value === "asc" || value === "desc",
  );

  useEffect(() => {
    dispatch(getDepositStatusesList());
  }, []);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getDepositsList({
          limit: currentLimit,
          offset: activePage - 1,
          ...(providers.length && {
            paymentProviderIds: providers.map((el) => el.value as number),
          }),
          ...(methods.length && {
            paymentMethodIds: methods.map((el) => el.value as number),
          }),
          ...(currencies.length && {
            currencies: currencies.map((el) => el.value as string),
          }),
          ...(statuses.length && {
            canonical_status: statuses.map((el) => el.value as string),
          }),
          ...(types.length && {
            operationTypes: types.map((el) => el.value as string),
          }),
          ...(getNumericFilter(depositId) !== undefined && {
            depositId: getNumericFilter(depositId),
          }),
          ...(parseTransactionIds(userId).length > 0 && {
            userIds: parseTransactionIds(userId),
          }),
          ...(externalRef && { externalRef }),
          ...(createdFrom && { createdAtFrom: toIsoDateTime(createdFrom) }),
          ...(createdTo && { createdAtTo: toIsoDateTime(createdTo) }),
          ...(completedFrom && {
            completedAtFrom: toIsoDateTime(completedFrom),
          }),
          ...(completedTo && { completedAtTo: toIsoDateTime(completedTo) }),
          ...(hasOrder && { order }),
        }),
      );
    }
  }, [currentLimit, dispatch, isListFetched]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const formattedList = paymentsDepositList.map((item) => ({
    id: item.id,
    userId: item.userId,
    type:
      item.depositType?.toLowerCase() === "regular"
        ? "Regular"
        : item.depositType?.toLowerCase() === "manual"
          ? "Manual"
          : item.depositType?.toLowerCase() === "adjustment"
            ? "Adjustment"
          : item.depositType || "—",
    attemptPaid: `${item.amountAttempt} / ${item.amountPaid}`,
    currency: item.currency,
    paymentMethod: item.paymentMethod || "—",
    externalRef: item.externalRef || "—",
    externalTransactionId: item.externalTransactionId || "—",
    status: paymentStatusLabel(item, statusesList),
    canonical_status: item.canonical_status,
    allowed_status_actions: item.allowed_status_actions,
    created: item.created || "—",
    completed: item.completedAt || item.completed || "—",
    paymentMethodImage:
      item.paymentMethodImage ?? item.paymentProviderImage ?? "",
    processingType: item.processingType,
  }));

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <DepositFilters
        selectedProviders={providers}
        handleUpdateProviders={(e) => setProviders(e)}
        selectedCurrencies={currencies}
        handleUpdateCurrencies={(e) => setCurrencies(e)}
        selectedMethods={methods}
        handleUpdateMethods={(e) => setMethods(e)}
        selectedStatuses={statuses}
        handleUpdateStatuses={(e) => setStatuses(e)}
        selectedTypes={types}
        handleUpdateTypes={setTypes}
        depositId={depositId}
        setDepositId={setDepositId}
        userId={userId}
        setUserId={setUserId}
        externalRef={externalRef}
        setExternalRef={setExternalRef}
        createdFrom={createdFrom}
        createdTo={createdTo}
        setCreatedRange={(from, to) => {
          setCreatedFrom(from);
          setCreatedTo(to);
        }}
        completedFrom={completedFrom}
        completedTo={completedTo}
        setCompletedRange={(from, to) => {
          setCompletedFrom(from);
          setCompletedTo(to);
        }}
        handleApply={handleApply}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={11} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap table">
            {paymentsDepositList.length === 0 && (
              <EmptyState message={t("noDepositTransactionsFound")} />
            )}

            {paymentsDepositList.length > 0 && (
              <Table
                data={formattedList}
                idField="id"
                order={order}
                selectedItems={selectedItems}
                onAllSelect={handleAllClick}
                onSelect={(id) => handleItemClick(id)}
                onSort={(id) => {
                  if (!id) return;
                  if (id === "attemptPaid") {
                    handleOrderClick("amountAttempt");
                    return;
                  }
                  if (id === "type") {
                    handleOrderClick("depositType");
                    return;
                  }
                  handleOrderClick(id as keyof typeof order);
                }}
                hasCheckbox={false}
                hasOptions={false}
                className={styles.depositsTable}
                processingId={processingItemId}
                columns={{
                  hiddenColumns: [
                    "processingType",
                    "paymentMethodImage",
                    "canonical_status",
                    "allowed_status_actions",
                  ],
                  sortingColumns: {
                    id: { value: "ID", hasSorting: true },
                    userId: { value: "User ID", hasSorting: true },
                    type: {
                      value: "Type",
                      hasSorting: true,
                      sortedBy: order.depositType,
                    },
                    attemptPaid: {
                      value: "Attempt / Paid",
                      hasSorting: true,
                      sortedBy: order.amountAttempt,
                    },
                    currency: { value: "Currency", hasSorting: true },
                    paymentMethod: {
                      value: "Payment Method",
                      hasSorting: true,
                    },
                    externalRef: { value: "External Ref" },
                    externalTransactionId: {
                      value: "External Transaction ID",
                    },
                    status: { value: "Status", hasSorting: true },
                    created: { value: "Created", hasSorting: true },
                    completed: { value: "Completed", hasSorting: true },
                  },
                }}
                customColumns={(row) => {
                  const statusOptions = paymentStatusOptions(row, statusesList);
                  const isDisabled = !canUpdate || !row.allowed_status_actions?.length;

                  return {
                    currency: <span>{row.currency}</span>,
                    externalRef: (
                      <div className="copyable-title-cell">
                        {row.externalRef !== "—" && (
                          <CopyToClipboard text={row.externalRef} />
                        )}
                        <span
                          className={styles.externalRef}
                          title={
                            row.externalRef === "—"
                              ? undefined
                              : row.externalRef
                          }
                        >
                          {compactReference(row.externalRef)}
                        </span>
                      </div>
                    ),
                    externalTransactionId: (
                      <div className="copyable-title-cell">
                        {row.externalTransactionId !== "—" && (
                          <CopyToClipboard text={row.externalTransactionId} />
                        )}
                        <span
                          className={styles.externalRef}
                          title={
                            row.externalTransactionId === "—"
                              ? undefined
                              : row.externalTransactionId
                          }
                        >
                          {compactReference(row.externalTransactionId)}
                        </span>
                      </div>
                    ),
                    paymentMethod: (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {row.paymentMethodImage && (
                          <img
                            src={row.paymentMethodImage}
                            alt=""
                            style={{
                              width: 20,
                              height: 20,
                              objectFit: "contain",
                            }}
                          />
                        )}
                        <span>{row.paymentMethod}</span>
                      </div>
                    ),
                    status: (
                      <div
                        className={`table-status-dropdown table-status-dropdown--deposits table-status-dropdown--${row.status?.toLowerCase()}`}
                      >
                        <DefaultDropdown
                          value={row.canonical_status}
                          selectedLabel={row.status}
                          options={statusOptions}
                          onSelect={async (value) => {
                            if (!canUpdate || !row.allowed_status_actions?.includes(String(value))) return;

                            if (
                              value === row.canonical_status
                            ) {
                              return;
                            }
                            const statusId = statusesList.find(item => item.value === value)?.statusId;
                            if (statusId == null) return;
                            try {
                              await dispatch(
                                modifyDepositStatus({
                                  id: row.id,
                                  statusId,
                                }),
                              ).unwrap();

                              dispatch(
                                getDepositsList({
                                  limit: currentLimit,
                                  offset: activePage - 1,
                                  ...(providers.length && {
                                    paymentProviderIds: providers.map(
                                      (el) => el.value as number,
                                    ),
                                  }),
                                  ...(methods.length && {
                                    paymentMethodIds: methods.map(
                                      (el) => el.value as number,
                                    ),
                                  }),
                                  ...(currencies.length && {
                                    currencies: currencies.map(
                                      (el) => el.value as string,
                                    ),
                                  }),
                                  ...(statuses.length && {
                                    canonical_status: statuses.map(
                                      (el) => el.value as string,
                                    ),
                                  }),
                                  ...(types.length && {
                                    operationTypes: types.map(
                                      (el) => el.value as string,
                                    ),
                                  }),
                                  ...(getNumericFilter(depositId) !==
                                    undefined && {
                                    depositId: getNumericFilter(depositId),
                                  }),
                                  ...(parseTransactionIds(userId).length > 0 && {
                                    userIds: parseTransactionIds(userId),
                                  }),
                                  ...(externalRef && { externalRef }),
                                  ...(createdFrom && {
                                    createdAtFrom: toIsoDateTime(createdFrom),
                                  }),
                                  ...(createdTo && {
                                    createdAtTo: toIsoDateTime(createdTo),
                                  }),
                                  ...(completedFrom && {
                                    completedAtFrom:
                                      toIsoDateTime(completedFrom),
                                  }),
                                  ...(completedTo && {
                                    completedAtTo: toIsoDateTime(completedTo),
                                  }),
                                  ...(hasOrder && { order }),
                                }),
                              );

                              dispatch(
                                showNotification({
                                  title: t("success"),
                                  message: t("entityUpdateSuccess"),
                                  type: NotificationTypes.success,
                                }),
                              );
                            } catch (error) {
                              handleApiError({
                                error,
                                t,
                                dispatch,
                              });
                            }
                            return;
                          }}
                          isSmall
                          disabled={isDisabled}
                        />
                      </div>
                    ),
                  };
                }}
              />
            )}
          </div>
        )}

        {isListFetched && paymentsDepositList.length > 0 && (
          <Pagination
            limitsArray={depositsListLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              dispatch(handlePaginate(1));
            }}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default DepositsList;
